import React, { useContext } from "react";

import { DispatchContext } from "../../../context/domain/planning/DispatchContext";
import { WeeklyContext } from "../../../context/global/WeeklyContext";
import { LockClosed } from "../../../components/icons/svg/outline";

import { formatDate } from "../../../utils/datetime";

/**
 * Week strip header for the daily dispatch grid: weekday labels, dates, and a
 * lock icon when that day is dispatch-locked. Reads visible week days from
 * {@link WeeklyContext} and lock state from {@link DispatchContext}.
 *
 * @param {Object} props - Component props.
 * @param {"absolute"|"stacked"} [props.layout="absolute"] Absolute overlays the
 *   grid (legacy/capture); stacked sits in normal flow under TopBar (full-page sticky).
 * @returns {JSX.Element}
 */
const DatesBar = ({ layout = "absolute" }) => {
  const { showWeekends, datesOfWeek } = useContext(WeeklyContext);
  const dispatchContext = useContext(DispatchContext);
  const dayDetails = dispatchContext?.dayDetails ?? null;

  const datesOfWeekStrs = datesOfWeek.map((date) =>
    formatDate(date, "MM-DD-YY")
  );

  const gridCols = showWeekends
    ? "grid-cols-7 min-w-dispatchWeekends"
    : "grid-cols-5 min-w-dispatch";
  // Stacked header uses Schedule day-strip chrome (bg-gray-100/50).
  const layoutClass =
    layout === "stacked"
      ? `relative z-20 grid w-full gap-0 border-b border-gray-300 bg-gray-50 ${gridCols}`
      : `absolute -left-2 z-20 grid w-[102%] gap-0 border-b border-gray-300 bg-gray-100 pl-2 ${gridCols}`;

  return (
    <div className={layoutClass}>
      {datesOfWeek.map((date, index) => {
        const lockedForDay = Boolean(
          dayDetails?.find(
            (dayRow) => dayRow.date === formatDate(date, "YYYY-MM-DD")
          )?.locked
        );
        return (
          <div
            key={index}
            className="m-1 my-2 border-r border-gray-300/80 text-center text-sm text-gray-700 last:border-r-0"
          >
            <p className="mb-0 mt-1 text-xs font-semibold uppercase leading-3 text-gray-800">
              <span className="relative inline-block">
                {formatDate(date, "weekday")}
                {lockedForDay ? (
                  <LockClosed
                    className="absolute left-full top-1/2 ml-1 h-3.5 w-3.5 -translate-y-1/2 text-gray-700"
                    aria-label="Locked day"
                  />
                ) : null}
              </span>
            </p>
            <p className="mt-1 text-xs font-medium leading-3 text-gray-700">
              {datesOfWeekStrs[index]}
            </p>
          </div>
        );
      })}
    </div>
  );
};

export default DatesBar;
